import { find, findIndex } from "lodash";
import React, { useEffect } from "react";
import { useState } from "react";
import { Button } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import { PlanningForm, SimpleModal } from "src/app/components";
import { PlanningStepsList } from "src/app/components/PlanningForm/PlanningStepsList";
import Resume, { TContentType } from "src/app/components/PlanningForm/resumes/ResumeKeywords";
import { IConfirmation } from "src/app/components/form/FormikStepper";
import {
    useListAvailableRDForT1,
    useThematicsByRDT1Options,
    useUrlByLandingAndHomePageOptions,
} from "src/app/hooks";
import { PlanningTransporter } from "src/app/transports/planning";
import {
    KeywordsByDayWithThematic,
    Planning,
    UpsertPlanningKeywordDTO,
} from "src/app/types/planning";

import {
    getKeywordFromString,
    getNLT1KeywordsByMonthWithThematics,
    getRandomKeywordsFromStringByMonth,
} from "src/app/utils/keywordsHelper";

export interface IPlanningModalContainer {
    handleClose?(): void;
    planning: Planning;
    showModal?: boolean;
}
const PlanningModalContainer = (props: IPlanningModalContainer) => {
    const { handleClose = () => {}, showModal = true, planning } = props;

    const { formatMessage } = useIntl();

    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [showResumeModal, setShowResumeModal] = useState(false);
    const [wasConfirmed, setWasConfirmed] = useState(false);
    const [data, setData] = useState<
        Planning & {
            contentType?: TContentType;
        }
    >(planning);

    const [kwNetlinking, setKwNetlinking] =
        useState<Array<KeywordsByDayWithThematic & { id?: number }>>();

    const { urlOptions } = useUrlByLandingAndHomePageOptions(
        planning.moneySite.id,
        !!planning?.orderType?.netlinking
    );

    const { rdAvailable, refresh: refreshRdAvailable } = useListAvailableRDForT1(
        planning?.moneySite?.id,
        false
    );

    const { thematicsOptions, refresh: refreshThematics } = useThematicsByRDT1Options(
        planning.moneySite.id,
        false
    );

    const { stepsList, maxSteps } = PlanningStepsList(planning, thematicsOptions);

    useEffect(() => {
        if (planning.orderType.netlinking && rdAvailable.length === 0) {
            refreshRdAvailable();
        }
        if (planning.orderType.netlinking && thematicsOptions.length === 0) {
            refreshThematics();
        }
    }, [showModal]);

    const refreshResume = () => {
        setShowResumeModal(false);
        document.getElementById(`submitButton_stepperForm${currentStep}`)?.click();
    };

    const handleResumeData = (_values: any) => {
        switch (stepsList[currentStep].api) {
            case "filling": {
                if (!data?.moneySite?.content?.filling) break;
                const keywordByDate = getRandomKeywordsFromStringByMonth(
                    _values.kwFilling,
                    data.moneySite.content.filling,
                    data.months
                );
                setData((currentData: any) => ({
                    ...currentData,
                    contentType: "filling",
                    kwFilling: keywordByDate,
                }));
                setShowResumeModal(true);
                break;
            }
            case "lead": {
                if (!data?.moneySite?.content?.lead) break;
                const keywordByDate = getRandomKeywordsFromStringByMonth(
                    _values.kwLead,
                    data.moneySite.content.lead,
                    data.months
                );
                setData((currentData: any) => ({
                    ...currentData,
                    contentType: "filling",
                    kwLead: keywordByDate,
                }));
                setShowResumeModal(true);
                break;
            }
            case "boost": {
                if (!data?.moneySite?.content?.boost) break;
                const keywordByDate = getRandomKeywordsFromStringByMonth(
                    _values.kwBoost,
                    data.moneySite.content.boost,
                    data.months
                );
                setData((currentData: any) => ({
                    ...currentData,
                    contentType: "boost",
                    kwBoost: keywordByDate,
                }));
                setShowResumeModal(true);
                break;
            }
            case "discover": {
                if (!data?.moneySite?.content?.discover) break;
                const keywordByDate = getRandomKeywordsFromStringByMonth(
                    _values.kwDiscover,
                    data.moneySite.content.discover,
                    data.months
                );
                setData((currentData: any) => ({
                    ...currentData,
                    contentType: "discover",
                    kwDiscover: keywordByDate,
                }));
                setShowResumeModal(true);
                break;
            }
            case "ranking": {
                if (!data?.moneySite?.content?.ranking) break;
                const rankingKeywordsChosenByDay = getRandomKeywordsFromStringByMonth(
                    _values.kwRanking,
                    data.moneySite.content.ranking,
                    data.months
                );
                setData((currentData: any) => ({
                    ...currentData,
                    contentType: "ranking",
                    kwRanking: rankingKeywordsChosenByDay,
                }));
                setShowResumeModal(true);
                break;
            }
            case "netlinking": {
                if (!data?.moneySite?.content?.netlinking) break;
                if (!rdAvailable) break;
                const netlinkingKeys = Object.keys(_values.kwNetlinking);

                let keywords = {};

                netlinkingKeys.forEach(
                    (el) =>
                        (keywords = {
                            ...keywords,
                            [parseInt(el.replace("thematicId", ""), 10)]: getKeywordFromString(
                                _values.kwNetlinking[el]
                            ),
                        })
                );
                getNLT1KeywordsByMonthWithThematics(
                    planning,
                    data.moneySite.content.netlinking,
                    keywords
                )
                    .then((res) => {
                        setKwNetlinking(
                            res.map((el, index) => {
                                const option = find(
                                    urlOptions,
                                    (url) => url.contentType === "HOME"
                                );
                                let url = "";
                                if (option) url = option.label;
                                return {
                                    ...el,
                                    id: index,
                                    link: {
                                        ...el.link,
                                        url,
                                    },
                                };
                            })
                        );
                        setData((currentData: any) => ({
                            ...currentData,
                            contentType: "netlinkingWithOptions",
                            kwNetlinking: res,
                        }));
                        setShowResumeModal(true);
                    })
                    .catch((error) => {
                        toast.error(
                            formatMessage({ id: `common.notification.error.${error.message}` })
                        );
                    });

                break;
            }
        }
    };

    const goToStep = (step: number) => {
        setCurrentStep(step);
    };

    const handleCloseResume = () => {
        setShowResumeModal(false);
    };

    const handleConfirmatedResume = () => {
        setShowResumeModal(false);
        setWasConfirmed(true);
    };

    const handleKwNetlinking = (id: number, url: string) => {
        const index = findIndex(kwNetlinking, (el) => el.id === id);
        const newKwnetlinking = kwNetlinking;
        if (!newKwnetlinking || !newKwnetlinking[index].link) return;
        newKwnetlinking[index].link = { ...newKwnetlinking[index].link, url };
        setKwNetlinking(newKwnetlinking);
    };

    const handleSubmit = async () => {
        if (!data) return;
        setLoading(true);
        const auxData = data;
        auxData.kwNetlinking = kwNetlinking;

        try {
            setLoading(true);
            await PlanningTransporter.save(auxData, data?.id);
            toast.success(formatMessage({ id: "common.notification.success" }));
            handleClose();
        } catch (err: any) {
            toast.error(formatMessage({ id: "common.notification.error" }));
        } finally {
            setLoading(false);
        }
    };

    const confirmation = (): IConfirmation => {
        return {
            step: currentStep,
            wasConfirmed,
            setWasConfirmed,
            confirmationRequest: (_values) => {
                handleResumeData(_values);
            },
            confirmationResponse: {},
            confirmationModalCondition: () => true,
        };
    };

    const confirmationLabel = () => {
        return !wasConfirmed
            ? formatMessage({ id: "common.action.seePlanning" })
            : currentStep === (maxSteps || 1) - 1
            ? formatMessage({ id: "common.action.savePlanning" })
            : formatMessage({ id: "common.action.continuePlanning" });
    };

    const FooterLeftComponent = () => {
        return (
            <div className="d-flex justify-content-end">
                {currentStep !== 0 ? (
                    <Button variant="secondary" onClick={() => goToStep(currentStep - 1)}>
                        <FormattedMessage id="common.action.return" />
                    </Button>
                ) : (
                    <Button variant="secondary" onClick={handleClose}>
                        <FormattedMessage id="common.action.close" />
                    </Button>
                )}
            </div>
        );
    };

    const ResumeFooterLeftComponent = () => {
        return (
            <div className="d-flex justify-content-end">
                <Button variant="secondary" onClick={refreshResume}>
                    <FormattedMessage id="common.action.otherPlanning" />
                </Button>
            </div>
        );
    };
    return (
        <>
            <SimpleModal
                entityName={formatMessage({ id: "common.entity.planning" })}
                formId={`stepperForm${currentStep}`}
                handleClose={() => {
                    goToStep(0);
                    handleClose();
                }}
                showModal={showModal}
                loading={loading}
                buttonConfirmationLabel={confirmationLabel()}
                modalProps={{ size: "xl" }}
                FooterLeftComponent={<FooterLeftComponent />}
            >
                <PlanningForm
                    initialValues={UpsertPlanningKeywordDTO.getDefault()}
                    currentStep={currentStep}
                    onSubmit={handleSubmit}
                    maxSteps={maxSteps}
                    stepsList={stepsList}
                    confirmation={confirmation()}
                    goToStep={goToStep}
                />
            </SimpleModal>
            <SimpleModal
                entityName={formatMessage({
                    id: `common.entity.${data?.contentType || ""}Summary`,
                })}
                formId="none"
                isSubModal={true}
                handleClose={handleCloseResume}
                buttonConfirmationAction={handleConfirmatedResume}
                showModal={showResumeModal}
                modalProps={{ size: "xl" }}
                FooterLeftComponent={<ResumeFooterLeftComponent />}
                buttonConfirmationLabel={formatMessage({ id: "common.action.AcceptPlanning" })}
            >
                {React.createElement(Resume, {
                    ...data,
                    kwNetlinking,
                    urlOptions,
                    thematicsOptions,
                    handleKwNetlinking,
                })}
            </SimpleModal>
        </>
    );
};

export default PlanningModalContainer;
